/**
 * Full-screen interstitial shown when the SPA loads in an external mobile
 * browser after a payment redirect (e.g. returning from PayPal).
 *
 * Offers the user a primary "Return to Smoove App" link that uses the
 * platform-appropriate deep-link mechanism (Android Intent URI / iOS
 * Universal Link tap), and a secondary "Continue in browser" fallback.
 */

import logoBlack from 'assets/png/logo-black.png';

type Props = {
  /** Platform-appropriate URL that opens the native app (intent:// or https://) */
  returnUrl: string;
  /** Called when the user chooses to stay in the browser */
  onContinueInBrowser: () => void;
  /** Payment outcome — controls the heading copy */
  variant: 'success' | 'cancel';
};

/**
 * Renders a fixed full-screen overlay prompting the user to return to
 * the Smoove mobile app. The primary action is an `<a>` tag (not a
 * button) because both Android Intent URIs and iOS Universal Links
 * require a real navigation / tap on a link element.
 */
export default function ReturnToAppInterstitial({ returnUrl, onContinueInBrowser, variant }: Props) {
  const heading = 'success' === variant ? 'Payment Completed' : 'Payment Cancelled';

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-6 text-center">
      <img src={logoBlack} alt="Smoove" className="mb-8 h-10" />

      <h1 className="mb-3 text-2xl font-bold text-gray-900">{heading}</h1>

      <p className="mb-8 max-w-sm text-sm text-gray-600">
        You are viewing this page in your browser. Tap below to return to the Smoove app.
      </p>

      {/* anchor tag required — intent:// URIs and Universal Link taps need <a href> navigation */}
      <a
        href={returnUrl}
        className="inline-flex items-center justify-center rounded-lg bg-brand-purple px-8 py-3 text-base font-semibold text-white shadow-sm hover:opacity-90"
      >
        Return to Smoove App
      </a>

      <button
        type="button"
        onClick={onContinueInBrowser}
        className="mt-4 text-sm font-medium text-gray-500 underline hover:text-gray-700"
      >
        Continue in browser
      </button>
    </div>
  );
}
